import { Dispatch, MouseEvent, RefObject, useRef, useState } from 'react';
import { AlertColor, Button, IconButton, Stack, TableCell, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import { VarianceChip } from 'components/InputField';
import { AddEditDiscountForm } from './AddEditDiscountForm';
import { ActualizationTableEmpty } from '../ActualizationTableEmpty';
import {
  actualizationTableSxFor,
  deductionColumnWidths,
  mobileDeductionColumnWidths,
  useActualizationMobile,
} from '../actualizationTableLayout';
import DrawerMenu from 'components/DrawerMenu';
import { removeErrorFieldsFromValues } from 'utils/validators';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Tabular from 'components/Tabular/Tabular';
import { FormComponent } from 'utils/validators/HelperComponents';
import { formatCurrency, getDrawerWidth } from 'utils/validators/HelperFunctions';
import { ToolTip } from 'components/Tooltip/ToolTip';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';

type TableComponentProps = {
  editedData: any;
  editRow: (idx: any) => void;
  deleteRow: (idx: any) => void;
  pastBooking: boolean;
  addOnCategories: any;
  previewDiscounts?: any[];
};

const qtyNumber = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const deductionValueDisplay = (row: any) => {
  if (row.price == null || row.price === '') {
    return { primary: '—', secondary: '' };
  }
  if (row.discountType === 'PERCENT') {
    return { primary: `${row.price}%`, secondary: '' };
  }
  return {
    primary: formatCurrency(qtyNumber(row.price)),
    secondary: strings.actualization_discount_flat_hint,
  };
};

const categoryLabel = (row: any, addOnCategories: any, previewName?: string) =>
  previewName ??
  addOnCategories?.find((opt: any) => String(opt.value) === String(row?.addOnCategory?.value ?? row?.addOnCategory))
    ?.label ??
  row?.addOnCategoryName ??
  row?.addOnCategory?.label ??
  (typeof row.addOnCategory === 'string' && row.addOnCategory ? row.addOnCategory : null) ??
  '—';

const amountLabel = (row: any, previewLine?: any) => {
  if (previewLine?.discountedAmount != null) return formatCurrency(qtyNumber(previewLine.discountedAmount));
  if (row.price == null || row.price === '') return '—';
  return row.discountType === 'PERCENT' ? `${row.price}%` : formatCurrency(qtyNumber(row.price));
};

const deductionVarianceAmount = (row: any, previewLine?: any) => {
  if (previewLine?.discountedAmount != null) return qtyNumber(previewLine.discountedAmount);
  if (row.discountType === 'PERCENT') return null;
  if (row.price == null || row.price === '') return null;
  return qtyNumber(row.price);
};

const TableComponent = ({
  editedData,
  editRow,
  deleteRow,
  pastBooking,
  addOnCategories,
  previewDiscounts,
}: TableComponentProps) => {
  const mobile = useActualizationMobile();
  const headers = [
    strings.description,
    ...(mobile ? [] : [strings.discountType]),
    strings.actualization_addon_amount,
    ...(mobile ? [] : [strings.actualization_addon_variance]),
    ...(pastBooking ? [''] : []),
  ];
  const columnAlign = headers.map((_, i) => (i === 0 ? 'left' : 'right')) as Array<'left' | 'right'>;
  const totalAmount = editedData.reduce((sum: number, row: any, idx: number) => {
    const previewAmount = previewDiscounts?.[idx]?.discountedAmount;
    if (previewAmount != null) return sum + qtyNumber(previewAmount);
    if (row.discountType === 'PERCENT') return sum;
    return sum + qtyNumber(row.price);
  }, 0);

  return (
    <Tabular
      size="small"
      headerColor="secondary"
      rows={headers}
      columnAlign={columnAlign}
      columnWidths={mobile ? mobileDeductionColumnWidths(pastBooking) : deductionColumnWidths(pastBooking)}
      sx={actualizationTableSxFor(mobile)}>
      {editedData.map((row: any, index: number) => {
        const previewLine = previewDiscounts?.[index];
        const category = categoryLabel(row, addOnCategories);
        const deductionValue = deductionValueDisplay(row);
        const varianceAmount = deductionVarianceAmount(row, previewLine);
        return (
          <TableRow
            key={row.id ?? `${row.description}-${index}`}
            hover={pastBooking}
            onClick={pastBooking ? () => editRow(index) : undefined}
            sx={
              pastBooking
                ? {
                    cursor: 'pointer',
                    '&:hover .actualization-delete-btn': { color: 'error.main' },
                  }
                : undefined
            }>
            <TableCell scope="row">
              <Stack spacing={0.125}>
                <Typography sx={{ fontSize: '13px', lineHeight: 1.25, fontWeight: 600 }}>
                  {row.description || '—'}
                </Typography>
                {category && category !== '—' ? (
                  <Typography color="text.secondary" sx={{ fontSize: '11px', lineHeight: 1.25 }}>
                    {category}
                  </Typography>
                ) : null}
              </Stack>
            </TableCell>
            {!mobile && (
              <TableCell align="right">
                <Stack alignItems="flex-end" spacing={0.125}>
                  <Typography sx={{ fontSize: '14px', lineHeight: 1.25, fontWeight: 500 }}>
                    {deductionValue.primary}
                  </Typography>
                  {deductionValue.secondary ? (
                    <Typography color="text.secondary" sx={{ fontSize: '10px', lineHeight: 1.25 }}>
                      {deductionValue.secondary}
                    </Typography>
                  ) : null}
                </Stack>
              </TableCell>
            )}
            <TableCell align="right">{amountLabel(row, previewLine)}</TableCell>
            {!mobile && (
              <TableCell align="right">
                {varianceAmount == null || varianceAmount === 0 ? (
                  '—'
                ) : (
                  <VarianceChip label={formatCurrency(Math.abs(varianceAmount))} tone="negative" />
                )}
              </TableCell>
            )}
            {pastBooking && (
              <TableCell align="right">
                <ToolTip title="Edit">
                  <IconButton
                    size="small"
                    sx={{ mr: 0.5, p: 0.25 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      editRow(index);
                    }}
                    aria-label="edit"
                    color="primary">
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
                <ToolTip title="Delete">
                  <IconButton
                    size="small"
                    className="actualization-delete-btn"
                    sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow(index);
                    }}
                    aria-label="delete"
                    color="primary">
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
              </TableCell>
            )}
          </TableRow>
        );
      })}
      <TableRow
        sx={{
          '& td': {
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: 1.25,
            py: 0.75,
            px: 1,
            boxShadow: theme => `inset 0 1px 0 ${theme.palette.divider}`,
          },
        }}>
        <TableCell colSpan={mobile ? 1 : 2}>{strings.actualization_discount_total}</TableCell>
        <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
        {!mobile && <TableCell align="right" />}
        {pastBooking && <TableCell align="right" />}
      </TableRow>
    </Tabular>
  );
};

type AddEditDiscountProps = {
  editedData: any;
  setData: Dispatch<any>;
  pastBooking: boolean;
  addOnCategories: any;
  previewDiscounts?: any[];
};

export const AddEditDiscount: React.FC<AddEditDiscountProps> = ({
  editedData = [],
  setData,
  pastBooking,
  addOnCategories,
  previewDiscounts,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [pageResponse, setPageResponse] = useState<any>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [message, setMessage] = useState<any>({
    display: false,
    severity: null,
    message: '',
  });

  let formRef: RefObject<Form | null | undefined> = useRef();

  const handleClose = () => {
    setOpenDrawer(false);
    setPageResponse({});
    formRef.current?.resetForm();
    setSelectedRowId(null);
    setHasError(false);
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(editedData));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);
    setData(deleteValue);
  };
  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData);
    
    // Transform addOnCategory to send only the value to API
    if (data.addOnCategory && typeof data.addOnCategory === 'object' && data.addOnCategory.value) {
      data.addOnCategory = data.addOnCategory.value;
    }
    
    setHasError(false);
    if (isFormValid) {
      if (data.discountType === 'FLAT' && String(data.price ?? '').trim().length > 10) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.flatDiscountError,
        });
      } else if (data.discountType === 'PERCENT' && parseInt(formData.price) > 100) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.percentDiscountError,
        });
      } else {
        const array = [...editedData];
        if (selectedRowId !== null) {
          array[selectedRowId] = { ...array[selectedRowId], ...data };
          setSelectedRowId(null);
        } else {
          array.push(data);
        }
        setData(array);
        setOpenDrawer(false);
        setPageResponse({});
      }
    } else {
      setHasError(true);
    }
  };

  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editRow = JSON.parse(JSON.stringify(editedData));
    const editValue = editRow.find((item: any, i: any) => i === idx);
    
    // Convert addOnCategory value back to option object for form
    if (editValue.addOnCategory && typeof editValue.addOnCategory === 'string') {
      const categoryOption = addOnCategories.find((cat: any) => cat.value === editValue.addOnCategory);
      if (categoryOption) {
        editValue.addOnCategory = categoryOption;
      }
    }
    
    setPageResponse(editValue);
    setOpenDrawer(true);
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      {(pastBooking || editedData?.length > 0) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5, mb: 1.5, minHeight: 36, gap: 1 }}>
          <Typography color="primary" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}>
            {strings.actualization_section_discount}
          </Typography>
          {pastBooking && (
            <Button
              onClick={() => setOpenDrawer(true)}
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              sx={{ minHeight: 36 }}>
              {strings.actualization_section_discount_add}
            </Button>
          )}
        </Stack>
      )}
      {editedData?.length ? (
        <TableComponent
          pastBooking={pastBooking}
          editedData={editedData}
          editRow={editRow}
          deleteRow={deleteRow}
          addOnCategories={addOnCategories}
          previewDiscounts={previewDiscounts}
        />
      ) : pastBooking ? (
        <ActualizationTableEmpty message={strings.actualization_section_discount_empty} />
      ) : null}
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={strings.discount}>
        <FormComponent
          hasError={hasError}
          formRef={formRef}
          pageResponse={pageResponse}
          handleSave={handleSave}
          formModel={AddEditDiscountForm(strings, addOnCategories)}
        />
        {selectedRowId !== null && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            sx={{ mt: 1.5 }}
            onClick={() => {
              deleteRow(selectedRowId);
              handleClose();
            }}>
            {strings.actualization_deduction_remove}
          </Button>
        )}
      </DrawerMenu>
    </>
  );
};
